import { useState, useEffect, useCallback, useRef } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, FileText, Search, Loader2, Download, RefreshCw, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import api, { authFetch } from '../lib/api';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  credited: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

function ReturnFormDialog({ open, onClose, onCreated }) {
  const [supplierName, setSupplierName] = useState('');
  const [poId, setPoId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ product_name: '', quantity: 1, unit_cost: 0, reason: '' }]);
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => setItems(prev => [...prev, { product_name: '', quantity: 1, unit_cost: 0, reason: '' }]);
  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, val) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

  const submit = async () => {
    if (!supplierName.trim()) { toast.error('Supplier name is required'); return; }
    const validItems = items.filter(it => it.product_name.trim() && it.quantity > 0);
    if (!validItems.length) { toast.error('Add at least one valid item'); return; }
    setSubmitting(true);
    try {
      await api.post('/supplier-returns', {
        supplier_name: supplierName.trim(),
        po_id: poId.trim() || null,
        notes: notes.trim() || null,
        items: validItems.map(it => ({
          product_id: `prod_${it.product_name.toLowerCase().replace(/\s+/g, '_')}`,
          product_name: it.product_name.trim(),
          quantity: parseFloat(it.quantity),
          unit_cost: parseFloat(it.unit_cost),
          reason: it.reason.trim() || null,
        })),
      });
      toast.success('Supplier return recorded');
      onCreated();
      onClose();
      setSupplierName(''); setPoId(''); setNotes('');
      setItems([{ product_name: '', quantity: 1, unit_cost: 0, reason: '' }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record return');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Supplier Return / Debit Note</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Supplier Name *</Label>
              <Input value={supplierName} onChange={e => setSupplierName(e.target.value)} placeholder="e.g. MedSupply Ltd" data-testid="return-supplier-name" />
            </div>
            <div className="space-y-1">
              <Label>Related PO # (optional)</Label>
              <Input value={poId} onChange={e => setPoId(e.target.value)} placeholder="PO-20260101-ABCDEF" data-testid="return-po-id" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Return Items *</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="return-add-item">+ Add Item</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end">
                  <div><Label className="text-xs">Product Name</Label>
                    <Input value={it.product_name} onChange={e => updateItem(idx, 'product_name', e.target.value)} placeholder="Product name" data-testid={`item-name-${idx}`} />
                  </div>
                  <div><Label className="text-xs">Qty</Label>
                    <Input type="number" min="0.01" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} data-testid={`item-qty-${idx}`} />
                  </div>
                  <div><Label className="text-xs">Unit Cost</Label>
                    <Input type="number" min="0" step="0.01" value={it.unit_cost} onChange={e => updateItem(idx, 'unit_cost', e.target.value)} data-testid={`item-cost-${idx}`} />
                  </div>
                  <div><Label className="text-xs">Reason</Label>
                    <Select value={it.reason} onValueChange={v => updateItem(idx, 'reason', v)}>
                      <SelectTrigger data-testid={`item-reason-${idx}`}><SelectValue placeholder="Reason" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="damaged">Damaged</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                        <SelectItem value="overstock">Overstock</SelectItem>
                        <SelectItem value="wrong_item">Wrong Item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} disabled={items.length === 1} className="text-red-500">×</Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total: GHc {items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_cost) || 0), 0).toFixed(2)}
            </p>
          </div>

          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes for the supplier..." rows={2} data-testid="return-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="return-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Record Return
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SupplierReturnsPage() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/supplier-returns', { params: { page, limit: 20 } });
      setReturns(res.data.returns || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch { toast.error('Failed to load returns'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (returnId) => {
    setDownloading(returnId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await authFetch(`${API_URL}/api/supplier-returns/${returnId}/pdf`);
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${returnId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download debit note'); }
    finally { setDownloading(null); }
  };

  const filtered = search ? returns.filter(r => r.return_id?.toLowerCase().includes(search.toLowerCase()) || r.supplier_name?.toLowerCase().includes(search.toLowerCase())) : returns;

  return (
    <div className="space-y-6 p-6" data-testid="supplier-returns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Supplier Returns</h1>
          <p className="text-sm text-muted-foreground">Manage goods returned to suppliers and generate debit notes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="returns-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => setShowForm(true)} data-testid="new-return-btn"><Plus className="w-4 h-4 mr-2" />New Return</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search returns…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="returns-search" />
            </div>
            <p className="text-sm text-muted-foreground">{total} return{total !== 1 ? 's' : ''}</p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Return ID</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No returns found</TableCell></TableRow>
                )}
                {filtered.map(ret => (
                  <TableRow key={ret.return_id} data-testid={`return-row-${ret.return_id}`}>
                    <TableCell className="font-mono text-xs font-medium">{ret.return_id}</TableCell>
                    <TableCell>{ret.supplier_name}</TableCell>
                    <TableCell>{ret.items?.length || 0} item{ret.items?.length !== 1 ? 's' : ''}</TableCell>
                    <TableCell>GHc {(ret.total_value || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[ret.status] || ''} data-testid={`return-status-${ret.return_id}`}>
                        {ret.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{ret.created_at?.slice(0, 10)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(ret.return_id)}
                        disabled={downloading === ret.return_id} data-testid={`download-debit-${ret.return_id}`}>
                        {downloading === ret.return_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        <span className="ml-1 hidden sm:inline">Debit Note</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm">Page {page} of {pages}</span>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ReturnFormDialog open={showForm} onClose={() => setShowForm(false)} onCreated={load} />
    </div>
  );
}
